import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, setAdminToken, getAdminToken } from "@/lib/api";
import { ArrowLeft, LogOut } from "lucide-react";

export default function AdminPage() {
  const [authed, setAuthed] = useState(Boolean(getAdminToken()));
  const [passcode, setPasscode] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    try {
      const res = await api.post("/admin/auth", { passcode });
      setAdminToken(res.data.token);
      setAuthed(true);
      toast.success("Welcome, host.");
    } catch {
      toast.error("Wrong passcode.");
    }
  };

  const handleLogout = () => {
    setAdminToken(null);
    setAuthed(false);
    navigate("/admin");
  };

  if (!authed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center container-kynn"
        data-testid="admin-login-page"
      >
        <form onSubmit={handleLogin} className="w-full max-w-md fade-up">
          <Link
            to="/"
            className="small-caps flex items-center gap-2 opacity-60 hover:opacity-100 mb-10"
            style={{ color: "#DDBFCF" }}
            data-testid="back-home-link"
          >
            <ArrowLeft size={12} /> Back to Kynn
          </Link>

          <div className="small-caps mb-4" style={{ color: "#B9929D" }}>
            Host access
          </div>
          <h1
            className="font-display mb-8"
            style={{ fontSize: "56px", lineHeight: 1, color: "#F2EEE7", letterSpacing: "-0.02em" }}
          >
            Only the<br />
            <em style={{ color: "#DDBFCF" }}>host</em> may enter.
          </h1>

          <label className="block small-caps mb-3" style={{ color: "#B9929D" }} htmlFor="passcode">
            Passcode
          </label>
          <input
            id="passcode"
            data-testid="admin-passcode-input"
            type="password"
            className="kynn-input"
            placeholder="Enter host passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
          />

          <button type="submit" data-testid="admin-login-btn" className="btn-primary mt-10">
            Enter dashboard →
          </button>
        </form>
      </div>
    );
  }

  const linkStyle = ({ isActive }) => ({
    background: isActive ? "#E2D3E2" : "transparent",
    color: isActive ? "#240F1C" : "#F2EEE7",
    borderRight: "1px solid rgba(221,191,207,0.35)",
  });

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="container-kynn pt-8 pb-6 flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <Link to="/" className="font-display text-2xl" style={{ color: "#F2EEE7" }}>Kynn</Link>
          <span className="small-caps" style={{ color: "#DDBFCF", opacity: 0.7 }}>
            — Host dashboard
          </span>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          className="small-caps flex items-center gap-1.5 opacity-70 hover:opacity-100"
          style={{ color: "#DDBFCF" }}
        >
          <LogOut size={12} /> Sign out
        </button>
      </header>

      <div className="hairline container-kynn" />

      {/* Tab nav */}
      <nav className="container-kynn pt-10">
        <div
          className="inline-flex overflow-hidden"
          style={{
            border: "1px solid rgba(221,191,207,0.35)",
            borderRadius: "8px",
          }}
        >
          <NavLink
            to="/admin/guests"
            data-testid="admin-tab-guests"
            className="small-caps px-6 py-3 transition-colors"
            style={({ isActive }) => ({ ...linkStyle({ isActive }) })}
          >
            Guest list
          </NavLink>
          <NavLink
            to="/admin/site"
            data-testid="admin-tab-site"
            className="small-caps px-6 py-3 transition-colors"
            style={({ isActive }) => ({ ...linkStyle({ isActive }), borderRight: "none" })}
          >
            Site settings
          </NavLink>
        </div>
      </nav>

      <Outlet context={{ setAuthed }} />
    </div>
  );
}
